import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { flowsApi } from '../services/api';
import { Flow } from '../types/automation';
import toast from 'react-hot-toast';

/**
 * Query keys para React Query cache management
 */
export const flowQueryKeys = {
  all: (channel: string, userId: string) => ['flows', channel, userId] as const,
  detail: (channel: string, flowId: string) => ['flows', channel, 'detail', flowId] as const,
  executions: (channel: string, flowId: string) => ['flows', channel, 'executions', flowId] as const,
};

/**
 * Hook reutilizável para gerenciar fluxos de automação
 * 
 * @param channel Canal da automação ('gmail', 'whatsapp', 'slack')
 * @param userId ID do usuário autenticado
 * @returns Métodos e estados para CRUD de fluxos
 * 
 * @example
 * ```tsx
 * const { flows, isLoading, createFlow, executeFlow } = useFlowsApi('gmail', userId);
 * 
 * // Criar novo fluxo
 * await createFlow({
 *   name: 'Boas-vindas',
 *   nodes: [...],
 *   connections: [...]
 * });
 * 
 * // Executar fluxo
 * await executeFlow('flow_123');
 * ```
 */
export const useFlowsApi = (channel: string, userId?: string) => {
  const queryClient = useQueryClient();
  
  /**
   * Query para listar fluxos do canal
   */
  const { data: flows, isLoading, error, refetch } = useQuery({
    queryKey: flowQueryKeys.all(channel, userId!),
    queryFn: async () => {
      const response = await flowsApi.listFlows(channel, userId!);
      return response.data || [];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
  
  /**
   * Mutation para criar novo fluxo
   */
  const createFlowMutation = useMutation({
    mutationFn: async (flow: Partial<Flow>) => {
      const response = await flowsApi.createFlow(channel, userId!, flow);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Fluxo criado com sucesso! 🎉');
      queryClient.invalidateQueries({ queryKey: flowQueryKeys.all(channel, userId!) });
    },
    onError: (error: any) => {
      toast.error(`Erro ao criar fluxo: ${error.message}`);
    },
  });
  
  /**
   * Mutation para atualizar fluxo
   */
  const updateFlowMutation = useMutation({
    mutationFn: async ({ flowId, flow }: { flowId: string; flow: Partial<Flow> }) => {
      const response = await flowsApi.updateFlow(channel, userId!, flowId, flow);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Fluxo atualizado! ✏️');
      queryClient.invalidateQueries({ queryKey: flowQueryKeys.all(channel, userId!) });
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar fluxo: ${error.message}`);
    },
  });
  
  /**
   * Mutation para deletar fluxo
   */
  const deleteFlowMutation = useMutation({
    mutationFn: async (flowId: string) => {
      await flowsApi.deleteFlow(channel, userId!, flowId);
    },
    onSuccess: () => {
      toast.success('Fluxo deletado! 🗑️');
      queryClient.invalidateQueries({ queryKey: flowQueryKeys.all(channel, userId!) });
    },
    onError: (error: any) => {
      toast.error(`Erro ao deletar fluxo: ${error.message}`);
    },
  });
  
  /**
   * Mutation para executar fluxo
   */
  const executeFlowMutation = useMutation({
    mutationFn: async ({ flowId, context }: { flowId: string; context?: Record<string, any> }) => {
      const response = await flowsApi.executeFlow(channel, userId!, flowId, context);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.status === 'success') {
        toast.success('Fluxo executado com sucesso! ✅');
      } else {
        toast.error('Execução falhou - verifique os logs');
      }
    },
    onError: (error: any) => {
      toast.error(`Erro ao executar fluxo: ${error.message}`);
    },
  });
  
  /**
   * Mutation para ativar/desativar fluxo
   */
  const toggleFlowMutation = useMutation({
    mutationFn: async (flowId: string) => {
      const response = await flowsApi.toggleFlow(channel, userId!, flowId);
      return response.data;
    },
    onSuccess: (data) => {
      const status = data.isActive ? 'ativado' : 'desativado';
      toast.success(`Fluxo ${status}! 🔄`);
      queryClient.invalidateQueries({ queryKey: flowQueryKeys.all(channel, userId!) });
    },
    onError: (error: any) => {
      toast.error(`Erro ao toggle fluxo: ${error.message}`);
    },
  });
  
  return {
    // Estados
    flows: flows || [],
    isLoading,
    error,
    
    // Métodos
    refetch,
    createFlow: createFlowMutation.mutateAsync,
    updateFlow: updateFlowMutation.mutateAsync,
    deleteFlow: deleteFlowMutation.mutateAsync,
    executeFlow: executeFlowMutation.mutateAsync,
    toggleFlow: toggleFlowMutation.mutateAsync,
    
    // Mutation states
    isCreating: createFlowMutation.isPending,
    isUpdating: updateFlowMutation.isPending,
    isDeleting: deleteFlowMutation.isPending,
    isExecuting: executeFlowMutation.isPending,
    isToggling: toggleFlowMutation.isPending,
  };
};
